import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@ooh/ui/components/sonner';
import type { BatchReleaseResponse, BatchReserveResponse } from '@ooh/contracts';
import type { MultiCellSelection, MultiSelectCellRef } from '../../../availability';
import type { UnifiedSelectCellResolution } from '../../../availability/lib/unified-select-types';
import { showBatchReserveConflictToasts } from '../../../reservations/lib/error-toast';
import {
  buildAvailabilityConflictKeyFromDate,
  dropConflictedAvailabilityCells,
  dropSkippedAvailabilityCells,
} from './inventory-selection-helpers';

export interface UseSelectBatchResultsOptions {
  periods: { id: string; start: string }[];
  narrowSelection: (
    updater: (current: MultiCellSelection | null) => MultiCellSelection | null,
  ) => void;
  resolveCell: (ref: MultiSelectCellRef) => UnifiedSelectCellResolution | null;
  /** Maps a conflicted media unit id to the unit id of this surface's cell refs; defaults to `String(id)`. */
  unitIdOf?: (mediaUnitId: number) => string | undefined;
  refreshAvailability: () => void;
}

export interface UseSelectBatchResultsResult {
  handleBatchReserveSuccess: (response: BatchReserveResponse) => void;
  handleBatchReleaseSuccess: (response: BatchReleaseResponse) => void;
}

const defaultUnitIdOf = (mediaUnitId: number): string => String(mediaUnitId);

export function useSelectBatchResults({
  periods,
  narrowSelection,
  resolveCell,
  unitIdOf = defaultUnitIdOf,
  refreshAvailability,
}: UseSelectBatchResultsOptions): UseSelectBatchResultsResult {
  const { t } = useTranslation('reservations');

  const handleBatchReserveSuccess = useCallback(
    (response: BatchReserveResponse) => {
      refreshAvailability();
      showBatchReserveConflictToasts(
        response.conflicted,
        t as (key: string, options?: Record<string, unknown>) => string,
      );
      const conflictedKeys = new Set<string>();
      for (const conflict of response.conflicted) {
        const unitId = unitIdOf(conflict.mediaUnitId);
        if (unitId !== undefined) {
          conflictedKeys.add(buildAvailabilityConflictKeyFromDate(unitId, conflict.dateFrom));
        }
      }
      narrowSelection((current) =>
        dropConflictedAvailabilityCells(current, conflictedKeys, periods),
      );
    },
    [refreshAvailability, t, unitIdOf, narrowSelection, periods],
  );

  const handleBatchReleaseSuccess = useCallback(
    (response: BatchReleaseResponse) => {
      refreshAvailability();
      if (response.skipped.length > 0) {
        toast.error(t('toasts.batchReleaseSkipped', { count: response.skipped.length }));
      }
      const skippedReservationIds = new Set(response.skipped.map((s) => s.reservationId));
      narrowSelection((current) =>
        dropSkippedAvailabilityCells(current, skippedReservationIds, resolveCell),
      );
    },
    [refreshAvailability, t, narrowSelection, resolveCell],
  );

  return { handleBatchReserveSuccess, handleBatchReleaseSuccess };
}
